import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './ClientPortal.css';
import { tcEldStatus, tcCamStatus } from './truckerCloud';

const API_BASE = window.location.port === '3000' ? 'http://localhost:3001' : '';

const BLANK_TRACTOR = () => ({ year: '', make: '', vin: '', value: '' });
const BLANK_TRAILER = () => ({ year: '', make: '', vin: '', value: '' });
const BLANK_DRIVER  = () => ({ name: '', dob: '', dl: '', state: '', exp: '', hireDate: '' });

const DOC_DEFS = [
  {
    id: 'lossRuns',
    label: 'Loss Runs (Last 5 Years — All Lines)',
    why: 'Required by all carriers. Shows your claims history across auto liability, cargo, and physical damage.',
    required: true,
  },
  {
    id: 'coi',
    label: 'Certificate of Insurance (Current)',
    why: 'Shows your current coverage so we can identify any gaps before renewal.',
    required: true,
  },
  {
    id: 'ifta',
    label: 'IFTA (Last 4 Quarters)',
    why: 'Verifies your annual mileage and operating states for rating purposes.',
    required: false,
  },
  {
    id: 'mvrs',
    label: 'MVRs (One Per Driver)',
    why: 'Motor Vehicle Records are required by carriers to evaluate each driver.',
    required: false,
  },
];

// ── TruckerCloud badge for portal ─────────────────────────────────────────────
function TCBadge({ status }) {
  if (status === 'yes')
    return (
      <span className="cp-tc-badge cp-tc-yes">
        ✓ TruckerCloud Compatible
      </span>
    );
  if (status === 'no')
    return (
      <span className="cp-tc-badge cp-tc-no">
        ⚠ Not currently listed as TruckerCloud compatible
      </span>
    );
  return null;
}

// ── Compute checklist from current form state ─────────────────────────────────
// NOTE: tractors/trailers/drivers are the live form state, already pre-populated
// from initialData on load — do NOT merge initialData arrays here or counts double.
function buildChecklist(initialData, tractors, trailers, drivers, commodities, uploadedIftaCount = 0) {
  const docs = (initialData || {}).docsOnFile || {};

  const items = [];

  // Documents — Loss Runs: use verification grid if available for detailed status
  const lrv = (initialData || {}).lossRunVerification;
  if (lrv) {
    const lrvLines = ['autoLiability', 'cargo', 'physicalDamage', 'generalLiability'];
    const lrvLabels = { autoLiability: 'AL', cargo: 'Cargo', physicalDamage: 'PD', generalLiability: 'GL' };
    const policyYears = ['2025-2026', '2024-2025', '2023-2024', '2022-2023', '2021-2022'];
    const missingParts = [];
    lrvLines.forEach(k => {
      const entries = lrv[k] || [];
      const missingYrs = policyYears.filter(y => !entries.find(e => e.year === y && e.present));
      if (missingYrs.length > 0) missingParts.push(`${lrvLabels[k]}: ${missingYrs.join(', ')}`);
    });
    const lrvComplete = missingParts.length === 0;
    items.push({
      key: 'lossRuns',
      label: 'Loss Runs — last 5 years, all lines (AL, Cargo, PD, GL)',
      have: lrvComplete,
      note: lrvComplete ? '' : `Still missing: ${missingParts.join(' | ')}`,
    });
  } else {
    items.push({ key: 'lossRuns', label: 'Loss Runs — last 5 years, all lines (AL, Cargo, PD, GL)', have: !!docs.lossRuns });
  }
  items.push({ key: 'coi',      label: 'Certificate of Insurance (current)',                       have: !!docs.coi });
  const iftaStored = typeof docs.ifta === 'number' ? docs.ifta : (docs.ifta ? 1 : 0);
  const iftaTotal  = iftaStored + uploadedIftaCount;
  items.push({
    key: 'ifta',
    label: 'IFTA — last 4 quarters',
    have: iftaTotal >= 4,
    partial: iftaTotal > 0 && iftaTotal < 4,
    note: iftaTotal > 0 && iftaTotal < 4 ? `${iftaTotal} of 4 quarters uploaded — ${4 - iftaTotal} more needed` : '',
  });
  items.push({ key: 'mvrs',     label: 'MVR for each driver',                                      have: !!docs.mvrs, optional: true });

  // Equipment — use form state only (already contains initialData values)
  const allEquip       = [...tractors, ...trailers].filter(t => t.year || t.make || t.vin);
  const missingValues  = allEquip.filter(t => !t.value || t.value === '');
  items.push({
    key: 'equipValues',
    label: 'Stated values for all equipment',
    have: allEquip.length > 0 && missingValues.length === 0,
    note: missingValues.length > 0 ? `${missingValues.length} unit${missingValues.length > 1 ? 's' : ''} missing value` : '',
  });

  // Drivers — use form state only (already contains initialData values)
  // Support both new field names (dl/exp/hireDate) and old App.js colKey names (dl_number/yrs_exp/hire_date)
  const drDL    = dr => dr.dl       || dr.dl_number || '';
  const drExp   = dr => dr.exp      || dr.yrs_exp   || '';
  const drHire  = dr => dr.hireDate || dr.hire_date || '';
  const activeDrivers = drivers.filter(dr => dr.name);
  const missingDL    = activeDrivers.filter(dr => !drDL(dr));
  const missingState = activeDrivers.filter(dr => !dr.state);
  const missingExp   = activeDrivers.filter(dr => !drExp(dr));
  const missingHire  = activeDrivers.filter(dr => !drHire(dr));

  items.push({
    key: 'driverDL',
    label: 'Driver license numbers and states',
    have: activeDrivers.length > 0 && missingDL.length === 0 && missingState.length === 0,
    note: missingDL.length > 0 ? `${missingDL.length} driver${missingDL.length > 1 ? 's' : ''} missing license #` : '',
  });
  items.push({
    key: 'driverExp',
    label: 'Years of CDL experience for each driver',
    have: activeDrivers.length > 0 && missingExp.length === 0,
    note: missingExp.length > 0 ? `${missingExp.length} driver${missingExp.length > 1 ? 's' : ''} missing experience` : '',
  });
  items.push({
    key: 'driverHire',
    label: 'Date of hire for each driver',
    have: activeDrivers.length > 0 && missingHire.length === 0,
    note: missingHire.length > 0 ? `${missingHire.length} driver${missingHire.length > 1 ? 's' : ''} missing hire date` : '',
  });

  // Commodities
  items.push({
    key: 'commodities',
    label: 'Commodities description — what you haul',
    have: !!commodities.trim(),
  });

  return items;
}

export default function ClientPortal() {
  const { portalId } = useParams();

  const [portal,     setPortal]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [submitted,  setSubmitted]  = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [tractors,      setTractors]      = useState([BLANK_TRACTOR()]);
  const [trailers,      setTrailers]      = useState([BLANK_TRAILER()]);
  const [drivers,       setDrivers]       = useState([BLANK_DRIVER()]);
  const [commodities,   setCommodities]   = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState({
    lossRuns: [], coi: [], ifta: [], mvrs: [],
  });
  const [commodityCheckboxes, setCommodityCheckboxes] = useState({
    flatBed:    { oversize: false, overweight: false, steel: false, steelCoils: false, oilfieldEquipment: false, logs: false },
    dryVan:     { amazonDelivery: false, householdGoods: false, doublesTriples: false, lastMileDelivery: false, electronics: false, liquor: false, usMail: false, cigarettes: false, designerClothing: false },
    reefer:     { seafood: false, eggs: false, dairy: false, shellfish: false, hangingMeat: false, frozenMeat: false, flowerNursery: false },
    autoHauler: { newVehicles: false, usedVehicles: false, dealerToManufacturer: false, dealerToDealer: false, exoticLuxury: false, classicAntique: false, avgCostPerVehicle: '' },
  });
  const [loadAgreements, setLoadAgreements] = useState('');
  const [fein, setFein]                     = useState('');
  const [eldsInstalled, setEldsInstalled]   = useState('');
  const [eldBrand, setEldBrand]             = useState('');
  const [dashcams, setDashcams]             = useState('');
  const [dashcamBrand, setDashcamBrand]     = useState('');
  const [ownerDob, setOwnerDob]                   = useState('');
  const [radiusOfOperation, setRadiusOfOperation] = useState('');
  const [equipSub, setEquipSub] = useState('Tractors');

  const fileRefs = useRef({});
  DOC_DEFS.forEach(d => {
    if (!fileRefs.current[d.id]) fileRefs.current[d.id] = React.createRef();
  });

  useEffect(() => {
    fetch(`${API_BASE}/api/portals/${portalId}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); setLoading(false); return; }
        setPortal(data);
        if (data.initialData?.tractors?.length) setTractors(data.initialData.tractors);
        if (data.initialData?.trailers?.length) setTrailers(data.initialData.trailers);
        if (data.initialData?.drivers?.length)  setDrivers(data.initialData.drivers);
        if (data.initialData?.commodities)      setCommodities(data.initialData.commodities);
        if (data.initialData?.commodityCheckboxes) {
          setCommodityCheckboxes(prev => {
            const src = data.initialData.commodityCheckboxes;
            return {
              flatBed:    { ...prev.flatBed,    ...(src.flatBed    || {}) },
              dryVan:     { ...prev.dryVan,     ...(src.dryVan     || {}) },
              reefer:     { ...prev.reefer,     ...(src.reefer     || {}) },
              autoHauler: { ...prev.autoHauler, ...(src.autoHauler || {}) },
            };
          });
        }
        if (data.initialData?.loadAgreements)    setLoadAgreements(data.initialData.loadAgreements);
        const ls = data.initialData?.leadSheet || {};
        if (ls.fein)          setFein(ls.fein);
        if (ls.eldsInstalled) setEldsInstalled(ls.eldsInstalled);
        if (ls.eldBrand)      setEldBrand(ls.eldBrand);
        if (ls.dashcams)      setDashcams(ls.dashcams);
        if (ls.dashcamBrand)         setDashcamBrand(ls.dashcamBrand);
        if (ls.ownerDob)             setOwnerDob(ls.ownerDob);
        if (ls.radiusOfOperation)    setRadiusOfOperation(ls.radiusOfOperation);
        setLoading(false);
      })
      .catch(() => { setError('Unable to load portal.'); setLoading(false); });
  }, [portalId]);

  // Progress bar
  // Progress counts required items only — MVRs excluded
  const progress = (() => {
    let done = 0;
    const total = 8;
    const reqDocs = ['lossRuns', 'coi', 'ifta'];
    if (reqDocs.some(id => uploadedFiles[id]?.length > 0)) done++;
    const equip = [...tractors, ...trailers].filter(t => t.vin || t.make);
    if (equip.length > 0 && equip.every(t => t.value)) done++;   // stated values
    const named = drivers.filter(d => d.name);
    const pDL   = d => d.dl       || d.dl_number || '';
    const pExp  = d => d.exp      || d.yrs_exp   || '';
    const pHire = d => d.hireDate || d.hire_date || '';
    if (named.length > 0) done++;                                             // drivers entered
    if (named.length > 0 && named.every(d => pDL(d) && d.state))  done++;   // DL + state
    if (named.length > 0 && named.every(d => pExp(d)))             done++;   // experience
    if (named.length > 0 && named.every(d => pHire(d)))            done++;   // hire date
    if (commodities.trim())                                       done++;
    if (reqDocs.every(id => uploadedFiles[id]?.length > 0))      done++; // all 3 docs
    return Math.round((done / total) * 100);
  })();

  // File handling
  const handleFileChange = (docId, e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadedFiles(prev => ({ ...prev, [docId]: [...(prev[docId] || []), ...files] }));
    e.target.value = '';
  };
  const removeFile = (docId, idx) =>
    setUploadedFiles(prev => ({ ...prev, [docId]: prev[docId].filter((_, i) => i !== idx) }));

  const updateRow = (setter, idx, field, val) =>
    setter(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  const delRow = (setter, idx) =>
    setter(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  // Submit
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const docIndex = {};
      for (const [cat, files] of Object.entries(uploadedFiles)) {
        docIndex[cat] = files.map(f => f.name);
      }
      const res = await fetch(`${API_BASE}/api/portals/${portalId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tractors, trailers, drivers, commodities, additionalInfo, documents: docIndex, commodityCheckboxes, loadAgreements, fein, ownerDob, radiusOfOperation, eldsInstalled, eldBrand, dashcams, dashcamBrand }),
      });
      let data;
      try { data = await res.json(); } catch { throw new Error(`Server error (${res.status})`); }
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setSubmitted(true);
    } catch (err) {
      alert('Submission failed: ' + err.message + '\nPlease try again or contact Carter directly.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── States ───────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="cp-page">
      <div className="cp-loading"><div className="cp-spinner" /><div>Loading your portal…</div></div>
    </div>
  );

  if (error || !portal) return (
    <div className="cp-page">
      <PortalHeader carrierName="" />
      <div className="cp-container">
        <div className="cp-section">
          <div className="cp-expired">
            <div className="cp-expired-icon">🔒</div>
            <div className="cp-expired-title">Portal Not Found</div>
            <div className="cp-expired-msg">This link is invalid or has expired. Please contact Carter Proffitt for a new link.</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!portal.active) return (
    <div className="cp-page">
      <PortalHeader carrierName={portal.carrierName} />
      <div className="cp-container">
        <div className="cp-section">
          <div className="cp-expired">
            <div className="cp-expired-icon">🔒</div>
            <div className="cp-expired-title">This Portal Has Closed</div>
            <div className="cp-expired-msg">This portal link is no longer active. Please contact Carter Proffitt if you need to submit additional information.</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="cp-page">
      <PortalHeader carrierName={portal.carrierName} />
      <div className="cp-container">
        <div className="cp-section">
          <div className="cp-confirmation">
            <div className="cp-confirmation-icon">✅</div>
            <div className="cp-confirmation-title">Thank you!</div>
            <div className="cp-confirmation-msg">
              Carter Proffitt has been notified and will be in touch shortly to review your submission and get your quotes started.
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Build checklist + determine which docs still need uploading
  const checklist = buildChecklist(portal.initialData, tractors, trailers, drivers, commodities, uploadedFiles.ifta?.length || 0);
  const docsOnFile = portal.initialData?.docsOnFile || {};
  const docsStillNeeded = DOC_DEFS.filter(d => !docsOnFile[d.id]);

  const haveItems        = checklist.filter(i => i.have);
  const partialItems     = checklist.filter(i => !i.have && i.partial);
  const needRequired     = checklist.filter(i => !i.have && !i.partial && !i.optional);
  const needOptional     = checklist.filter(i => !i.have && !i.partial && i.optional);

  // ── Full portal form ─────────────────────────────────────────────────────────
  return (
    <div className="cp-page">
      <PortalHeader carrierName={portal.carrierName} progress={progress} />

      <div className="cp-container">

        {/* Status Checklist */}
        <div className="cp-section">
          <div className="cp-section-header">
            <div className="cp-section-num">📋</div>
            <div className="cp-section-title">What We Need From You</div>
          </div>
          <div className="cp-section-body">
            {needRequired.length === 0 ? (
              <div className="cp-checklist-complete">
                🎉 All required items are complete! Review the sections below and hit Submit when ready.
              </div>
            ) : (
              <div className="cp-checklist-intro">
                Please provide the items marked in red below. Items in green are already on file.
              </div>
            )}

            {needRequired.length > 0 && (
              <div className="cp-checklist-group">
                <div className="cp-checklist-group-label cp-checklist-need-label">Required — still needed</div>
                {needRequired.map(item => (
                  <div key={item.key} className="cp-checklist-item cp-checklist-need">
                    <span className="cp-checklist-icon">✗</span>
                    <span className="cp-checklist-text">
                      {item.label}
                      {item.note && <span className="cp-checklist-note"> — {item.note}</span>}
                    </span>
                    <span className="cp-checklist-badge cp-checklist-badge-required">Required</span>
                  </div>
                ))}
              </div>
            )}

            {partialItems.length > 0 && (
              <div className="cp-checklist-group" style={{ marginTop: needRequired.length > 0 ? 14 : 0 }}>
                <div className="cp-checklist-group-label cp-checklist-partial-label">Partially uploaded — more needed</div>
                {partialItems.map(item => (
                  <div key={item.key} className="cp-checklist-item cp-checklist-partial cp-checklist-partial-expandable">
                    <span className="cp-checklist-icon">◑</span>
                    <span className="cp-checklist-text">
                      {item.label}
                      {item.note && <span className="cp-checklist-note-amber"> — {item.note}</span>}
                    </span>
                    <span className="cp-checklist-badge cp-checklist-badge-partial">Incomplete</span>
                    {item.key === 'ifta' && (
                      <div className="cp-checklist-partial-upload" onClick={e => e.stopPropagation()}>
                        {(uploadedFiles.ifta || []).length > 0 && (
                          <div className="cp-doc-files">
                            {(uploadedFiles.ifta || []).map((f, i) => (
                              <div key={i} className="cp-doc-file-item">
                                <span>📄 {f.name}</span>
                                <button className="cp-doc-file-remove" onClick={() => removeFile('ifta', i)}>✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <button
                          className="cp-doc-upload-btn"
                          onClick={() => fileRefs.current['ifta']?.current?.click()}
                        >
                          + Upload More IFTA Files
                        </button>
                        <input
                          ref={fileRefs.current['ifta']}
                          type="file"
                          multiple
                          className="cp-doc-hidden-input"
                          onChange={e => handleFileChange('ifta', e)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {needOptional.length > 0 && (
              <div className="cp-checklist-group" style={{ marginTop: (needRequired.length > 0 || partialItems.length > 0) ? 14 : 0 }}>
                <div className="cp-checklist-group-label cp-checklist-optional-label">Helpful — not required</div>
                {needOptional.map(item => (
                  <div key={item.key} className="cp-checklist-item cp-checklist-optional">
                    <span className="cp-checklist-icon">○</span>
                    <span className="cp-checklist-text">{item.label}</span>
                    <span className="cp-checklist-badge cp-checklist-badge-optional">Optional</span>
                  </div>
                ))}
              </div>
            )}

            {haveItems.length > 0 && (
              <div className="cp-checklist-group" style={{ marginTop: 14 }}>
                <div className="cp-checklist-group-label cp-checklist-have-label">Already on file</div>
                {haveItems.map(item => (
                  <div key={item.key} className="cp-checklist-item cp-checklist-have">
                    <span className="cp-checklist-icon">✓</span>
                    <span className="cp-checklist-text">{item.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section 1 — Document Uploads (only docs still needed) */}
        {docsStillNeeded.length > 0 && (
          <div className="cp-section">
            <div className="cp-section-header">
              <div className="cp-section-num">1</div>
              <div className="cp-section-title">Document Uploads</div>
            </div>
            <div className="cp-section-body">
              {docsStillNeeded.map(doc => {
                const files = uploadedFiles[doc.id] || [];
                return (
                  <div
                    key={doc.id}
                    className={`cp-doc-item${files.length ? ' uploaded' : ''}`}
                    onClick={() => fileRefs.current[doc.id]?.current?.click()}
                  >
                    <div className="cp-doc-item-header">
                      <div className="cp-doc-check">{files.length ? '✓' : ''}</div>
                      <div className="cp-doc-label">{doc.label}</div>
                      {!doc.required && <span className="cp-doc-optional">Optional</span>}
                    </div>
                    <div className="cp-doc-why">{doc.why}</div>
                    {files.length > 0 && (
                      <div className="cp-doc-files" onClick={e => e.stopPropagation()}>
                        {files.map((f, i) => (
                          <div key={i} className="cp-doc-file-item">
                            <span>📄 {f.name}</span>
                            <button className="cp-doc-file-remove" onClick={() => removeFile(doc.id, i)}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div onClick={e => e.stopPropagation()}>
                      <button
                        className="cp-doc-upload-btn"
                        onClick={e => { e.stopPropagation(); fileRefs.current[doc.id]?.current?.click(); }}
                      >
                        {files.length ? '+ Add More Files' : '+ Upload Files'}
                      </button>
                    </div>
                    <input
                      ref={fileRefs.current[doc.id]}
                      type="file"
                      multiple
                      className="cp-doc-hidden-input"
                      onChange={e => handleFileChange(doc.id, e)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Section 2 — Equipment */}
        <div className="cp-section">
          <div className="cp-section-header">
            <div className="cp-section-num">2</div>
            <div className="cp-section-title">Equipment List</div>
          </div>
          <div className="cp-section-body">
            <div className="cp-section-note">
              Please verify your equipment and fill in any missing <strong>Stated Values</strong> — carriers require a value for each unit.
            </div>
            <div className="cp-equip-subtabs">
              <button className={`cp-equip-subtab${equipSub === 'Tractors' ? ' active' : ''}`} onClick={() => setEquipSub('Tractors')}>
                Tractors ({tractors.length})
              </button>
              <button className={`cp-equip-subtab${equipSub === 'Trailers' ? ' active' : ''}`} onClick={() => setEquipSub('Trailers')}>
                Trailers ({trailers.length})
              </button>
            </div>
            {equipSub === 'Tractors' ? (
              <EquipTable rows={tractors} onUpdate={(i, f, v) => updateRow(setTractors, i, f, v)} onDel={i => delRow(setTractors, i)} onAdd={() => setTractors(p => [...p, BLANK_TRACTOR()])} label="tractor" />
            ) : (
              <EquipTable rows={trailers} onUpdate={(i, f, v) => updateRow(setTrailers, i, f, v)} onDel={i => delRow(setTrailers, i)} onAdd={() => setTrailers(p => [...p, BLANK_TRAILER()])} label="trailer" />
            )}
          </div>
        </div>

        {/* Section 3 — Drivers */}
        <div className="cp-section">
          <div className="cp-section-header">
            <div className="cp-section-num">3</div>
            <div className="cp-section-title">Driver List</div>
          </div>
          <div className="cp-section-body">
            <div className="cp-section-note">
              Please fill in <strong>license numbers, states, years of CDL experience, and hire dates</strong> for every driver — these are required by all carriers.
            </div>
            <DriverTable rows={drivers} onUpdate={(i, f, v) => updateRow(setDrivers, i, f, v)} onDel={i => delRow(setDrivers, i)} onAdd={() => setDrivers(p => [...p, BLANK_DRIVER()])} />
          </div>
        </div>

        {/* Section 4 — Commodities */}
        <div className="cp-section">
          <div className="cp-section-header">
            <div className="cp-section-num">4</div>
            <div className="cp-section-title">Commodities Hauled</div>
          </div>
          <div className="cp-section-body">
            <label className="cp-textarea-label">
              What do you primarily haul? If you haul multiple types, please give an approximate percentage breakdown.
            </label>
            <textarea
              className="cp-textarea"
              rows={4}
              value={commodities}
              onChange={e => setCommodities(e.target.value)}
              placeholder="e.g. General Freight 60%, Automotive Parts 25%, Refrigerated 15%"
            />
            <div className="cp-example">Include shipper names if you work with well-known companies — it helps carriers evaluate your risk.</div>
          </div>
        </div>

        {/* Section 5 — Commodity Checkboxes */}
        <div className="cp-section">
          <div className="cp-section-header">
            <div className="cp-section-num">5</div>
            <div className="cp-section-title">Commodity Details</div>
          </div>
          <div className="cp-section-body">
            <div className="cp-section-note">Check all that apply to your operation. This helps us match you with the right carriers.</div>
            {[
              { key: 'flatBed', label: 'Flat Bed', items: [
                { key: 'oversize', label: 'Oversize' }, { key: 'overweight', label: 'Overweight' },
                { key: 'steel', label: 'Steel' }, { key: 'steelCoils', label: 'Steel Coils' },
                { key: 'oilfieldEquipment', label: 'Oilfield Equipment' }, { key: 'logs', label: 'Logs' },
              ]},
              { key: 'dryVan', label: 'Dry Van / Box Truck', items: [
                { key: 'amazonDelivery', label: 'Amazon Delivery' }, { key: 'householdGoods', label: 'Household Goods' },
                { key: 'doublesTriples', label: 'Doubles/Triples' }, { key: 'lastMileDelivery', label: 'Last Mile Delivery' },
                { key: 'electronics', label: 'Electronics' }, { key: 'liquor', label: 'Liquor' },
                { key: 'usMail', label: 'US Mail' }, { key: 'cigarettes', label: 'Cigarettes' },
                { key: 'designerClothing', label: 'Designer Clothing' },
              ]},
              { key: 'reefer', label: 'Reefer Truck', items: [
                { key: 'seafood', label: 'Seafood' }, { key: 'eggs', label: 'Eggs' },
                { key: 'dairy', label: 'Dairy' }, { key: 'shellfish', label: 'Shellfish' },
                { key: 'hangingMeat', label: 'Hanging Meat' }, { key: 'frozenMeat', label: 'Frozen Meat' },
                { key: 'flowerNursery', label: 'Flower/Nursery' },
              ]},
              { key: 'autoHauler', label: 'Auto Hauler', items: [
                { key: 'newVehicles', label: 'New Vehicles' }, { key: 'usedVehicles', label: 'Used Vehicles' },
                { key: 'dealerToManufacturer', label: 'Dealer to Manufacturer' }, { key: 'dealerToDealer', label: 'Dealer to Dealer' },
                { key: 'exoticLuxury', label: 'Exotic/Luxury' }, { key: 'classicAntique', label: 'Classic/Antique' },
              ]},
            ].map(group => (
              <div key={group.key} className="cp-commodity-group">
                <div className="cp-commodity-group-title">{group.label}</div>
                <div className="cp-commodity-items">
                  {group.items.map(item => (
                    <label key={item.key} className="cp-checkbox-item">
                      <input type="checkbox"
                        checked={!!commodityCheckboxes[group.key]?.[item.key]}
                        onChange={e => setCommodityCheckboxes(prev => ({
                          ...prev,
                          [group.key]: { ...prev[group.key], [item.key]: e.target.checked }
                        }))}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                  {group.key === 'autoHauler' && (
                    <div className="cp-avg-cost">
                      <label>Avg cost per vehicle: </label>
                      <input className="cp-avg-cost-input"
                        value={commodityCheckboxes.autoHauler?.avgCostPerVehicle || ''}
                        onChange={e => setCommodityCheckboxes(prev => ({
                          ...prev, autoHauler: { ...prev.autoHauler, avgCostPerVehicle: e.target.value }
                        }))}
                        placeholder="$00,000" />
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div className="cp-load-agreements">
              <label className="cp-textarea-label">What companies do you have load agreements or contracts with?</label>
              <textarea className="cp-textarea" rows={3} value={loadAgreements}
                onChange={e => setLoadAgreements(e.target.value)}
                placeholder="e.g. Amazon DSP, Walmart, Convoy, direct shippers…" />
            </div>
          </div>
        </div>

        {/* Section 6 — Equipment & Compliance */}
        <div className="cp-section">
          <div className="cp-section-header">
            <div className="cp-section-num">6</div>
            <div className="cp-section-title">Equipment &amp; Compliance</div>
          </div>
          <div className="cp-section-body">
            <div className="cp-compliance-grid">
              <div className="cp-compliance-field">
                <label className="cp-textarea-label">Federal Employer Identification Number (FEIN)</label>
                <input
                  className="cp-compliance-input"
                  value={fein}
                  onChange={e => setFein(e.target.value)}
                  placeholder="XX-XXXXXXX"
                />
              </div>

              <div className="cp-compliance-field">
                <label className="cp-textarea-label">Owner Date of Birth</label>
                <input
                  type="date"
                  className="cp-compliance-input"
                  value={ownerDob}
                  onChange={e => setOwnerDob(e.target.value)}
                />
              </div>

              <div className="cp-compliance-field">
                <label className="cp-textarea-label">Radius of Operation</label>
                <input
                  className="cp-compliance-input"
                  value={radiusOfOperation}
                  onChange={e => setRadiusOfOperation(e.target.value)}
                  placeholder="e.g. 250 miles, Regional, OTR, Local"
                />
              </div>

              <div className="cp-compliance-field">
                <label className="cp-textarea-label">Trucks Equipped with ELDs?</label>
                <div className="cp-yn-row">
                  <button className={`cp-yn-btn${eldsInstalled === 'yes' ? ' active' : ''}`} onClick={() => setEldsInstalled('yes')}>Yes</button>
                  <button className={`cp-yn-btn${eldsInstalled === 'no'  ? ' active' : ''}`} onClick={() => setEldsInstalled('no')}>No</button>
                </div>
                {eldsInstalled === 'yes' && (
                  <>
                    <input
                      className="cp-compliance-input cp-compliance-sub-input"
                      value={eldBrand}
                      onChange={e => setEldBrand(e.target.value)}
                      placeholder="ELD Provider — e.g. Samsara, KeepTruckin, Omnitracs"
                    />
                    <TCBadge status={tcEldStatus(eldBrand)} />
                  </>
                )}
              </div>

              <div className="cp-compliance-field">
                <label className="cp-textarea-label">Trucks Equipped with Dashcams?</label>
                <div className="cp-yn-row">
                  <button className={`cp-yn-btn${dashcams === 'yes' ? ' active' : ''}`} onClick={() => setDashcams('yes')}>Yes</button>
                  <button className={`cp-yn-btn${dashcams === 'no'  ? ' active' : ''}`} onClick={() => setDashcams('no')}>No</button>
                </div>
                {dashcams === 'yes' && (
                  <>
                    <input
                      className="cp-compliance-input cp-compliance-sub-input"
                      value={dashcamBrand}
                      onChange={e => setDashcamBrand(e.target.value)}
                      placeholder="Dashcam Provider — e.g. Lytx, Netradyne, SmartWitness"
                    />
                    <TCBadge status={tcCamStatus(dashcamBrand)} />
                  </>
                )}
              </div>
            </div>

            <div className="cp-tc-infobox">
              <div className="cp-tc-infobox-icon">🔗</div>
              <div className="cp-tc-infobox-text">
                <strong>Why this matters:</strong> Many insurance carriers now require a TruckerCloud connection to access your ELD telematics data. Using a compatible provider can qualify you for better rates and faster quote turnaround.
              </div>
            </div>
          </div>
        </div>

        {/* Section 7 — Additional Info */}
        <div className="cp-section">
          <div className="cp-section-header">
            <div className="cp-section-num">7</div>
            <div className="cp-section-title">Additional Information</div>
          </div>
          <div className="cp-section-body">
            <label className="cp-textarea-label">
              Anything else you'd like Carter to know? Any recent safety improvements, new equipment, changes in operations, or questions?
            </label>
            <textarea
              className="cp-textarea"
              rows={4}
              value={additionalInfo}
              onChange={e => setAdditionalInfo(e.target.value)}
              placeholder="Optional — add any notes or questions here"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="cp-section">
          <div className="cp-section-body cp-submit-section">
            <button className="cp-submit-btn" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit to Carter Proffitt'}
            </button>
            {submitting && <div className="cp-submitting-note">Saving your information, please wait…</div>}
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function PortalHeader({ carrierName, progress }) {
  return (
    <div className="cp-header">
      <div className="cp-header-logo">Carter Proffitt</div>
      <div className="cp-header-agent">Carter Proffitt</div>
      <div className="cp-header-agency">Commercial Trucking Insurance Specialist</div>
      {carrierName && (
        <div className="cp-header-welcome">
          Hi {carrierName}, please use this portal to submit your insurance information securely.
        </div>
      )}
      {progress !== undefined && (
        <div className="cp-progress-bar-wrap">
          <div className="cp-progress-label">
            <span>Submission Progress</span>
            <span>{progress}%</span>
          </div>
          <div className="cp-progress-track">
            <div className="cp-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}
      <div className="cp-security-note">
        🔒 This is a secure portal. Your information is only shared with Carter Proffitt.
      </div>
    </div>
  );
}

function EquipTable({ rows, onUpdate, onDel, onAdd, label }) {
  return (
    <>
      <div className="cp-table-wrap">
        <table className="cp-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>Make / Model</th>
              <th>VIN</th>
              <th>Stated Value ($) *</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td><input className="cp-cell-input" value={row.year}  onChange={e => onUpdate(i, 'year',  e.target.value)} placeholder="2020" /></td>
                <td><input className="cp-cell-input" value={row.make}  onChange={e => onUpdate(i, 'make',  e.target.value)} placeholder="Kenworth T680" /></td>
                <td><input className="cp-cell-input" value={row.vin}   onChange={e => onUpdate(i, 'vin',   e.target.value)} placeholder="1XKAD49XXXXX" /></td>
                <td><input className={`cp-cell-input${!row.value ? ' cp-cell-missing' : ''}`} value={row.value} onChange={e => onUpdate(i, 'value', e.target.value)} placeholder="$120,000" /></td>
                <td><button className="cp-del-btn" onClick={() => onDel(i)} title={`Remove ${label}`}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button className="cp-add-row-btn" onClick={onAdd}>+ Add {label}</button>
    </>
  );
}

function DriverTable({ rows, onUpdate, onDel, onAdd }) {
  return (
    <>
      <div className="cp-table-wrap">
        <table className="cp-table">
          <thead>
            <tr>
              <th>Full Name</th>
              <th>Date of Birth</th>
              <th>DL Number *</th>
              <th>State *</th>
              <th>Yrs CDL *</th>
              <th>Year Hired *</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td><input className="cp-cell-input" value={row.name}     onChange={e => onUpdate(i, 'name',     e.target.value)} placeholder="John Smith" /></td>
                <td><input className="cp-cell-input" value={row.dob}      onChange={e => onUpdate(i, 'dob',      e.target.value)} placeholder="01/15/1985" /></td>
                <td><input className={`cp-cell-input${!(row.dl||row.dl_number) ? ' cp-cell-missing' : ''}`}      value={row.dl||row.dl_number||''}      onChange={e => onUpdate(i, 'dl',      e.target.value)} placeholder="DL123456" /></td>
                <td><input className={`cp-cell-input${!row.state ? ' cp-cell-missing' : ''}`}   value={row.state||''}   onChange={e => onUpdate(i, 'state',   e.target.value)} placeholder="TX" style={{ maxWidth: 60 }} /></td>
                <td><input className={`cp-cell-input${!(row.exp||row.yrs_exp) ? ' cp-cell-missing' : ''}`}     value={row.exp||row.yrs_exp||''}     onChange={e => onUpdate(i, 'exp',     e.target.value)} placeholder="8" style={{ maxWidth: 60 }} /></td>
                <td><input className={`cp-cell-input${!(row.hireDate||row.hire_date) ? ' cp-cell-missing' : ''}`} value={row.hireDate||row.hire_date||''} onChange={e => onUpdate(i, 'hireDate', e.target.value)} placeholder="2018" /></td>
                <td><button className="cp-del-btn" onClick={() => onDel(i)} title="Remove driver">✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button className="cp-add-row-btn" onClick={onAdd}>+ Add driver</button>
    </>
  );
}
